import { useQueryClient } from '@tanstack/react-query';
import { FC, FormEvent } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import Button, { RemoveButton } from '@/components/Button';
import { FormLayout, Larger } from '@/components/Forms';
import FormField from '@/components/Forms/FormField';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import { EditorialFacebookPostResource } from '@/types/resources';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';
import { RootState } from '@/utils/store';

import PostVariationField from './PostVariationField';

const AddButtonHolder = styled.div`
  text-align: right;
  margin-bottom: 8px;
  svg {
    margin-right: 8px;
  }
  button {
    width: 100%;
  }
`;

const VariationWrap = styled(Larger)<{ $isGray: boolean }>`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-row-gap: 24px;
  grid-column-gap: 24px;
  margin: 0 -32px;
  padding: 0 32px;

  ${({ $isGray }) =>
    $isGray &&
    css`
      padding: 24px 32px;
      background-color: ${({ theme }) => theme.colors.grayscaleSuperLight};
    `}
`;

const VariationHeading = styled(Larger)`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const NumberBadge = styled(Text)`
  display: flex;
  height: 24px;
  width: 48px;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.colors.purpleLighter};
  border-radius: 100px;
`;

type PostVariation = {
  channel_id?: number;
  repost_channel_id: null | number;
  published_at: string;
  site_visit_benchmark: number;
  reach_duration: number;
  is_instant: boolean;
};

export interface FormDataT {
  message: string;
  comment: string;
  link: string;
  owner: string;
  variations: PostVariation[];
}

interface Props {
  onClose: () => void;
  schedule?: EditorialFacebookPostResource;
}

const ScheduleModal: FC<Props> = ({ onClose, schedule }) => {
  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const userName = useSelector((state: RootState) => state.auth.user!.name);

  const {
    handleSubmit,
    clearErrors,
    control,
    setError,
    setValue,
    formState: { isSubmitting },
  } = useForm<FormDataT>({
    defaultValues: {
      message: schedule?.message ?? '',
      comment: '',
      link: schedule?.link ?? '',
      owner: userName,
      variations: [
        {
          published_at: '',
          channel_id: schedule?.channel.id,
          repost_channel_id: schedule?.repostChannelId ?? null,
          site_visit_benchmark: schedule?.siteVisitBenchmark ?? 0,
          reach_duration: schedule?.reachDuration ?? 60,
          is_instant: false,
        },
      ],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'variations',
  });

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    clearErrors();
    return handleSubmit(async (data) => {
      try {
        await api.post(`/v1/editorial/facebook-posts`, data);

        await queryClient.invalidateQueries(
          ['editorial_distribution_schedules'],
          {
            exact: false,
          },
        );

        onClose();

        alerts.success('A new post has been created successfully');
      } catch (err) {
        handleFormErrors<FormDataT>(err, setError, alerts, true);
      }
    })(e);
  };

  return (
    <Modal>
      <SidePanel heading={`New Post`} onClose={onClose}>
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <FormField<FormDataT>
                label="Message"
                type="textarea"
                control={control}
                name="message"
              />
              <FormField<FormDataT>
                label="Comment Message"
                type="textarea"
                control={control}
                name="comment"
              />
              <FormField<FormDataT>
                label="Article Link"
                control={control}
                name="link"
                inputType="url"
              />
              <FormField<FormDataT>
                label="Owner"
                control={control}
                name="owner"
              />
              {fields.length === 1 ? (
                <PostVariationField
                  control={control}
                  setValue={setValue}
                  key={fields[0].id}
                  index={0}
                />
              ) : (
                fields.map((field, i) => (
                  <VariationWrap $isGray={(i + 1) % 2 === 0}>
                    <VariationHeading>
                      <NumberBadge $type="smallBold" $color="white">
                        Nr. {i + 1}
                      </NumberBadge>
                      {fields.length > 1 && (
                        <RemoveButton
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            remove(i);
                          }}
                        >
                          Remove
                        </RemoveButton>
                      )}
                    </VariationHeading>
                    <PostVariationField
                      control={control}
                      setValue={setValue}
                      key={field.id}
                      index={i}
                    />
                  </VariationWrap>
                ))
              )}
              <Larger>
                <AddButtonHolder>
                  <Button
                    type="button"
                    $type="colorized"
                    onClick={() =>
                      append({
                        published_at: '',
                        site_visit_benchmark: 0,
                        reach_duration: 60,
                        is_instant: false,
                        repost_channel_id: null,
                      })
                    }
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 16 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M8 2V14"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M2 8H14"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                    Add additional page & time
                  </Button>
                </AddButtonHolder>
              </Larger>
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button
              disabled={isSubmitting}
              loading={isSubmitting}
              type="submit"
            >
              Schedule
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default ScheduleModal;
